/*
 * Interface language.
 *
 * One control sets everything: the answer-language picker chooses the
 * language Gia answers in AND the language of the interface. Auto follows the
 * browser's language for both.
 *
 * Strings sit in two buckets, because they are not translated to the same
 * depth:
 *
 *   ui   (i18n/ui)   sidebar, toolbar, composer, quota and upgrade wall,
 *                    help, the sourcing sheet: every picker language.
 *   calc (i18n/calc) the calculators, their worksheet and saved results, and
 *                    the messages about numbers that could not be read:
 *                    English and Spanish only, because the engineering terms
 *                    need a reviewer who knows both the language and RAS
 *                    design. A reader in another language gets the
 *                    calculators in English, or in Spanish when their browser
 *                    is Spanish.
 *
 * So a Thai operator sees a Thai sidebar and English calculators, and that is
 * deliberate: a wrong word in "un-ionised ammonia" or "as CaCO₃" is an
 * engineering error, not a cosmetic one.
 *
 * Legal and disclaimer text is in neither bucket. It stays English in every
 * language, with one translated line (legal.englishGoverning) saying the
 * English version governs.
 *
 * Keys are flat and dotted; a key starting "calc." or "saved." is calculator
 * text and is looked up in the calc bucket. A missing key falls back to
 * English, then to the key itself, so nothing can render blank.
 */
import { createContext, useContext } from 'react';
import uiEn from './ui/en';
import uiEs from './ui/es';
import uiPt from './ui/pt';
import uiVi from './ui/vi';
import uiId from './ui/id';
import uiZh from './ui/zh';
import uiTh from './ui/th';
import uiTr from './ui/tr';
import calcEn from './calc/en';
import calcEs from './calc/es';
import { calcStrings } from './calcStrings';

export const UI_STRINGS = Object.freeze({ en: uiEn, es: uiEs, pt: uiPt, vi: uiVi, id: uiId, zh: uiZh, th: uiTh, tr: uiTr });
export const CALC_STRINGS = Object.freeze({ en: calcEn, es: calcEs });

const has = (obj, k) => Object.prototype.hasOwnProperty.call(obj, k);
const isCalcKey = (key) => String(key).startsWith('calc.') || String(key).startsWith('saved.');

/** The language of the reader's browser, as a bare code ("es-CL" gives "es"). */
export function browserLanguage() {
  try {
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    const tag = (nav && ((nav.languages && nav.languages[0]) || nav.language)) || 'en';
    return String(tag).toLowerCase().split('-')[0];
  } catch {
    return 'en';
  }
}

/**
 * Which language each bucket renders in, for a picked answer language.
 * Auto means "follow the browser" for both.
 */
export function resolveLanguages(answerLang, browserLang = browserLanguage()) {
  const picked = !answerLang || answerLang === 'auto' ? browserLang : answerLang;
  const uiLang = has(UI_STRINGS, picked) ? picked : 'en';
  const calcLang = has(CALC_STRINGS, picked)
    ? picked
    : has(CALC_STRINGS, browserLang) && browserLang !== 'en'
      ? browserLang
      : 'en';
  return { uiLang, calcLang };
}

/** Kept for callers that only care about the interface language. */
export const uiLanguageFor = (answerLang, browserLang) => resolveLanguages(answerLang, browserLang).uiLang;

/*
 * Numbers inside a sentence follow the language: 1,500 in English and Thai,
 * 1.500 in Spanish, Portuguese, Vietnamese, Indonesian and Turkish.
 */
const formatNumber = (x, lang) => {
  try {
    return new Intl.NumberFormat(lang).format(x);
  } catch {
    return String(x);
  }
};

const fill = (s, vars, lang) =>
  vars
    ? String(s).replace(/\{(\w+)\}/g, (m, k) => {
        const v = vars[k];
        if (v == null) return m;
        return typeof v === 'number' && Number.isFinite(v) ? formatNumber(v, lang) : String(v);
      })
    : s;

/*
 * Plural form for this language and count. English and Spanish split at one;
 * Portuguese and Turkish likewise; Chinese, Thai, Vietnamese and Indonesian
 * have one form, so their files carry only _other and every count lands there.
 */
const pluralCategory = (lang, count) => {
  try {
    return new Intl.PluralRules(lang).select(count);
  } catch {
    return count === 1 ? 'one' : 'other';
  }
};

/**
 * t(key, vars). With vars.count, the plural form for the language is used
 * when the file has one. Numbers in vars are formatted for the language.
 */
export function makeT(uiLang, calcLang = uiLang) {
  const t = (key, vars) => {
    const calc = isCalcKey(key);
    const lang = calc ? calcLang : uiLang;
    const bucket = calc ? CALC_STRINGS : UI_STRINGS;
    const dict = bucket[lang] || bucket.en;
    const lookup = (k) => (has(dict, k) ? dict[k] : has(bucket.en, k) ? bucket.en[k] : null);
    if (vars && typeof vars.count === 'number') {
      const form = lookup(`${key}_${pluralCategory(lang, vars.count)}`) ?? lookup(`${key}_other`);
      if (form != null) return fill(form, vars, lang);
    }
    const s = lookup(key);
    return s != null ? fill(s, vars, lang) : key;
  };
  t.uiLang = uiLang;
  t.calcLang = calcLang;
  return t;
}

/*
 * Calculator text is translated at DISPLAY time, keyed by the English text,
 * because the calculator definitions are fingerprinted (calcFingerprint.js):
 * rewriting a label inside them would mark every saved result as stale.
 * Results saved or sent to Gia stay English; only what is on screen changes.
 */
export function makeTc(lang) {
  const table = calcStrings[lang];
  const tc = (s) => {
    if (!table || typeof s !== 'string' || !s) return s;
    if (has(table.exact, s)) return table.exact[s];
    for (const [re, rep] of table.patterns) {
      if (re.test(s)) return s.replace(re, rep);
    }
    return s;
  };
  tc.lang = lang;
  return tc;
}

/*
 * A figure the calculator produced, in the reader's number format. Only a
 * bare number is touched ("1,500.5"), never text that merely contains one:
 * an equation, a reference like "eq. 3.9b" or a range like "1.67–3.33 m/h"
 * must survive exactly as written.
 */
const BARE_NUMBER = /^-?\d{1,3}(,\d{3})+(\.\d+)?$|^-?\d+(\.\d+)?$/;
export function makeNum(lang) {
  const num = (s) => {
    if (typeof s !== 'string' || !BARE_NUMBER.test(s.trim())) return s;
    const x = Number(s.trim().replace(/,/g, ''));
    if (!Number.isFinite(x)) return s;
    const decimals = (s.split('.')[1] || '').length;
    try {
      return new Intl.NumberFormat(lang, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(x);
    } catch {
      return s;
    }
  };
  num.lang = lang;
  return num;
}

const I18nContext = createContext({
  t: makeT('en'),
  tc: makeTc('en'),
  num: makeNum('en'),
  uiLang: 'en',
  calcLang: 'en',
});
export const I18nProvider = I18nContext.Provider;
export const useI18n = () => useContext(I18nContext);
export const useT = () => useContext(I18nContext).t;
export const useTc = () => useContext(I18nContext).tc;

/**
 * A translated sentence with React elements in its placeholders, for text
 * that wraps part of itself in a link or a button.
 */
export function tNodes(t, key, nodes) {
  const parts = String(t(key)).split(/\{(\w+)\}/);
  return parts.map((p, i) =>
    i % 2 === 1 ? (nodes[p] !== undefined ? <span key={i}>{nodes[p]}</span> : `{${p}}`) : p
  );
}
